import React, { useState, useEffect } from "react";
import { ClipboardList, Loader2, AlertCircle, Search, Filter, Download } from "lucide-react";

const AuditLogs = () => {
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchAuditData = async () => {
      try {
        setLoading(true);
        const response = await fetch("/api/security/get_audit");
        if (!response.ok) {
          throw new Error('Failed to fetch audit logs');
        }
        const data = await response.json();
        
        // Parse the nested JSON string in the signin property
        let parsedData = [];
        if (data.signin) {
          parsedData = typeof data.signin === 'string' ? 
            JSON.parse(data.signin) : 
            data.signin;
        }
        
        // Sort by date (newest first)
        parsedData.sort((a, b) => new Date(b.Date) - new Date(a.Date));
        
        setAuditData(parsedData);
      } catch (err) {
        console.error('Error fetching audit logs:', err);
        setError("Failed to fetch audit logs");
      } finally {
        setLoading(false);
      }
    };

    fetchAuditData();
  }, []);

  const getStatusColor = (status) => {
    return status === 'Success' ? '#10b981' : '#ef4444';
  };

  const getStatusBgColor = (status) => {
    return status === 'Success' ? '#d1fae5' : '#fee2e2';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Filter data based on search and status
  const filteredData = auditData.filter(log => {
    const matchesSearch = searchTerm === '' ||
      Object.values(log).some(val =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      );
    const matchesStatus = filterStatus === 'all' || log.Status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-blue)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--cares-orange)' }} />
          <span style={{ color: 'var(--cares-orange)', fontSize: '14px', fontWeight: '500' }}>{error}</span>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '18px',
            height: '18px',
            color: 'var(--cares-text-note)'
          }} />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Filter style={{ width: '18px', height: '18px', color: 'var(--cares-text-label)' }} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'var(--cares-surface-card)',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Status</option>
            <option value="Success">Success</option>
            <option value="Failure">Failure</option>
          </select>
        </div>
        <div style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Showing <span style={{ fontWeight: '600', color: 'var(--cares-text-1)' }}>{filteredData.length}</span> of <span style={{ fontWeight: '600', color: 'var(--cares-text-1)' }}>{auditData.length}</span>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '2px solid var(--cares-line-card)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Service</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activity</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((log, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: index < filteredData.length - 1 ? '1px solid var(--cares-line-card)' : 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                  >
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--cares-text-1)', whiteSpace: 'nowrap' }}>
                      {formatDate(log.Date)}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--cares-text-1)' }}>
                      {log.Service}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--cares-text-1)' }}>
                      {log.Category}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--cares-text-1)' }}>
                      {log.Activity}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: getStatusColor(log.Status),
                        background: getStatusBgColor(log.Status)
                      }}>
                        {log.Status}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--cares-text-label)' }}>
                      {log['Status reason'] || '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', fontSize: '14px', color: 'var(--cares-text-note)' }}>
                    {searchTerm || filterStatus !== 'all' ? 'No logs match your filters' : 'No audit logs available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;